import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import QualityNexusLogo from "../QualityNexusLogo";
import ThemeSwitcher from "../ThemeSwitcher";
import LanguageSwitcher from "../LanguageSwitcher";
import { useLanguage } from "@/contexts/LanguageContext";
import { PLATFORM_ROUTES } from "@/lib/platform";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t, language } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [scrolled]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const isLinkActive = (path: string) => {
    return location.pathname === path;
  };

  const activeLinkClasses = "text-nexus-teal font-medium border-b-2 border-nexus-teal pb-1";
  const defaultLinkClasses = "text-nexus-gray dark:text-white hover:text-nexus-teal dark:hover:text-nexus-teal transition-colors";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white backdrop-blur-sm shadow-sm dark:bg-nexus-dark/90 dark:shadow-nexus-dark/20"
          : "bg-white dark:bg-nexus-dark/90"
      }`}
    >
      <div className="container flex items-center justify-between py-4 md:py-6">
        <Link to="/" className="flex items-center gap-2 group">
          <div className="group-hover:animate-rocket dark:logo-container">
            <QualityNexusLogo className="h-10 w-10" />
          </div>
          <span className="text-xl font-semibold text-nexus-gray dark:text-white">
            Quality <span className="font-bold">Nexus</span>
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          <div className={`flex items-center ${language === "ar" ? "space-x-reverse space-x-6" : "space-x-6"}`}>
            <Link to="/community" className={isLinkActive("/community") ? activeLinkClasses : defaultLinkClasses}>
              {t("nav.community")}
            </Link>
            <Link to="/services" className={isLinkActive("/services") ? activeLinkClasses : defaultLinkClasses}>
              {t("nav.services")}
            </Link>
            <Link to="/events" className={isLinkActive("/events") ? activeLinkClasses : defaultLinkClasses}>
              {t("nav.events")}
            </Link>
            <Link to="/blog" className={isLinkActive("/blog") ? activeLinkClasses : defaultLinkClasses}>
              {t("nav.blog")}
            </Link>
            <Link to="/contact" className={isLinkActive("/contact") ? activeLinkClasses : defaultLinkClasses}>
              {t("nav.contact")}
            </Link>
          </div>
          <div className="flex items-center space-x-3">
            <LanguageSwitcher />
            <ThemeSwitcher />
            <Button asChild variant="outline" size="sm" className="dark:border-white/20 dark:text-white dark:hover:bg-white/10">
              <a href={PLATFORM_ROUTES.login} target="_blank" rel="noopener noreferrer">{t("nav.signin")}</a>
            </Button>
            <Button asChild className="bg-nexus-brand hover:bg-nexus-brand/90" size="sm">
              <a href={PLATFORM_ROUTES.signup} target="_blank" rel="noopener noreferrer">{t("nav.getStarted")}</a>
            </Button>
          </div>
        </nav>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center gap-2">
          <LanguageSwitcher />
          <ThemeSwitcher />
          <button
            className="text-nexus-gray dark:text-white rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nexus-teal focus-visible:ring-offset-2"
            onClick={toggleMenu}
            aria-label={
              language === "ar"
                ? isOpen ? "إغلاق قائمة التنقل" : "فتح قائمة التنقل"
                : isOpen ? "Close navigation menu" : "Open navigation menu"
            }
            aria-expanded={isOpen}
            aria-controls="mobile-navigation"
          >
            {isOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div id="mobile-navigation" className="md:hidden bg-white dark:bg-nexus-dark border-t dark:border-white/10">
          <div className="container py-4 flex flex-col space-y-4">
            <Link
              to="/community"
              className={isLinkActive("/community") ? "px-4 py-2 " + activeLinkClasses : "px-4 py-2 " + defaultLinkClasses}
              onClick={toggleMenu}
            >
              {t("nav.community")}
            </Link>
            <Link
              to="/services"
              className={isLinkActive("/services") ? "px-4 py-2 " + activeLinkClasses : "px-4 py-2 " + defaultLinkClasses}
              onClick={toggleMenu}
            >
              {t("nav.services")}
            </Link>
            <Link
              to="/events"
              className={isLinkActive("/events") ? "px-4 py-2 " + activeLinkClasses : "px-4 py-2 " + defaultLinkClasses}
              onClick={toggleMenu}
            >
              {t("nav.events")}
            </Link>
            <Link
              to="/blog"
              className={isLinkActive("/blog") ? "px-4 py-2 " + activeLinkClasses : "px-4 py-2 " + defaultLinkClasses}
              onClick={toggleMenu}
            >
              {t("nav.blog")}
            </Link>
            <Link
              to="/contact"
              className={isLinkActive("/contact") ? "px-4 py-2 " + activeLinkClasses : "px-4 py-2 " + defaultLinkClasses}
              onClick={toggleMenu}
            >
              {t("nav.contact")}
            </Link>
            <div className="flex flex-col space-y-2 pt-2 border-t dark:border-white/10">
              <Button asChild variant="outline" size="sm" className="w-full dark:border-white/20 dark:text-white dark:hover:bg-white/10">
                <a href={PLATFORM_ROUTES.login} target="_blank" rel="noopener noreferrer">{t("nav.signin")}</a>
              </Button>
              <Button asChild className="bg-nexus-brand hover:bg-nexus-brand/90 w-full" size="sm">
                <a href={PLATFORM_ROUTES.signup} target="_blank" rel="noopener noreferrer">{t("nav.getStarted")}</a>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
